/**
 * Fleet-map "VMG meter loss/gain vs reference boat".
 *
 * When a reference boat is chosen in Map Settings, the fleet timeline switches from plotting a raw
 * channel (BSP/TWS/VMG) to plotting each boat's **cumulative VMG-made-good distance in meters,
 * relative to the reference boat**, integrated from the start of the visible time window.
 *
 *   positive => this boat has gained meters on the reference boat
 *   negative => this boat has lost meters to the reference boat
 *   reference boat itself => flat line at 0
 *
 * The first {@link REFERENCE_DELTA_WARMUP_MS} of the window (the start sequence / first two minutes of
 * a race) is a warmup: every boat reads exactly 0 there, and the integration only begins at the end of
 * it. Boats are still lining up and accelerating off the line, so meters accumulated in that phase say
 * nothing about relative speed and only offset the whole trace. The warmup is a `warmupMs` argument,
 * not a hard rule: callers pass 0 when the window is a brushed sub-range rather than the full race
 * (there is no start sequence to skip mid-race) — see `vmgDeltaInputs` in MultiMapTimeSeries.
 *
 * This is NOT geometric separation on the water and involves no lat/lon projection. Each boat's
 * "VMG distance sailed" is the time-integral of its **unsigned** VMG magnitude (made-good speed
 * toward whatever mark it is working); the plotted delta is that distance minus the reference boat's
 * distance, interpolated to each boat's own timestamps. Using the magnitude (rather than signing by a
 * shared leg objective) keeps the comparison correct when boats are on different legs — an overtaking
 * boat a leg ahead keeps gaining instead of being mis-signed as sailing the wrong way.
 *
 * Every sample is sanity-gated before it can move the integral, because one bad row would otherwise
 * offset a boat's entire trace against the fleet: see {@link MAX_MADE_GOOD_SPEED_KPH} (implausible
 * speeds are never integrated), {@link MAX_SAMPLE_DISTANCE_M} (no single sample may contribute more
 * than this), and {@link MAX_SPEED_HOLD_MS} (a short unusable burst holds the last known speed; a
 * longer one is reported as NaN rather than silently counted as zero progress).
 *
 * The public entry point is {@link applyReferenceVmgDeltaToGroups}.
 */
import { vmgMagnitudeInDisplayUnit, bspMagnitudeInDisplayUnit, SPEED_UNIT_KPH } from '../../../../utils/speedUnits';

/** km/h -> m/s. */
const KPH_TO_MPS = 1 / 3.6;
/**
 * Intervals longer than this contribute 0 meters (no fabricated distance across data dropouts).
 * Deliberately matched to the timeline chart's line-break/gap policy (`gapThresholdMs` in
 * MultiMapTimeSeries render()).
 */
export const TIMELINE_VMG_GAP_THRESHOLD_MS = 3000;
/**
 * Warmup at the head of the window during which every boat's delta is pinned to 0; the integration
 * starts at `t0 + this`. Two minutes covers the start sequence / first two minutes of a race.
 */
export const REFERENCE_DELTA_WARMUP_MS = 2 * 60 * 1000;
/**
 * Legs measured with boat speed (BSP) instead of |VMG|.
 *
 * On a reaching leg the mark is ~abeam of the wind, so `VMG = Bsp·cos(TWA)` collapses toward 0 and
 * badly under-measures real progress along the leg. Leg 1 is the reach off the start, so it is
 * integrated on BSP; every other leg is a beat or a run, where |VMG| is the right measure.
 *
 * This is deliberately a fixed course property rather than a per-boat decision. It used to be
 * inferred from each boat's own mean |TWA| landing in an 80–110° band, which meant the SAME leg
 * could be measured differently on different boats — on 2026-06-21 leg 7 came out as a reach for
 * AUS/CAN/DEN/ESP/FRA/ITA/NZL and as a beat for GER/SUI/SWE/USA, so boats sailing side by side were
 * compared on different scales. Two boats must always be measured the same way at the same instant.
 */
export const BSP_LEG_NUMBERS: readonly number[] = [1];

/**
 * Plausibility ceiling for a single sample's made-good speed (km/h).
 *
 * A GP50 tops out around 100 km/h; measured over 15 days of healthy production-shaped data
 * (331,080 in-window samples, 188 day blobs) the largest made-good sample was 92.4 km/h and the
 * largest raw |Bsp| was 101.1 km/h, so nothing in good data comes near this. Matched to
 * `LIVE_TRACK_MAX_JUMP_SPEED_KPH` in LiveTrackLayer, which draws the same line for position jumps.
 *
 * Anything above it is a broken sample, not a fast boat — an unconverted sentinel (±999), a decode
 * glitch, or a unit mix-up. Such a sample must never be integrated: at the 3 s gap threshold a
 * single 999 km/h row would inject 832 m of fabricated distance and permanently offset the boat's
 * whole trace against the rest of the fleet.
 */
export const MAX_MADE_GOOD_SPEED_KPH = 150;

/**
 * Hard ceiling on the distance one sample may contribute (m) = {@link MAX_MADE_GOOD_SPEED_KPH} over
 * {@link TIMELINE_VMG_GAP_THRESHOLD_MS}. The speed guard already bounds this, so it is a second,
 * independent backstop: it holds even if a caller widens the gap threshold, and it is the invariant
 * that actually matters downstream — no single sample can move a boat's curve by more than this.
 */
export const MAX_SAMPLE_DISTANCE_M = (MAX_MADE_GOOD_SPEED_KPH / 3.6) * (TIMELINE_VMG_GAP_THRESHOLD_MS / 1000);

/**
 * How long the last plausible speed may be carried across unusable samples (ms).
 *
 * An unusable sample means "we don't know how fast this boat was going", NOT "it stopped". Treating
 * it as zero is what produces the reported symptom — a boat reading a 1000 m loss while sitting
 * 100 m from the fleet on the water — because every dropped second silently credits the rest of the
 * fleet with ~11 m. For a short burst, holding the last known speed is far closer to the truth than
 * either zero or a fabricated spike, and leaves no step in the curve. Past this window the boat's
 * progress is genuinely unknown and the delta is reported as NaN instead of guessed at.
 */
export const MAX_SPEED_HOLD_MS = 3000;

/** Minimal row/group shapes this module reads. Rows are the raw fleet map samples. */
export interface MapPointRow {
  [key: string]: unknown;
}
export interface SourceGroup {
  sourceId: number;
  data: MapPointRow[];
  [key: string]: unknown;
}

/**
 * Normalize a row timestamp (Date | epoch ms/seconds | ISO string) to epoch ms.
 * Numeric seconds vs ms disambiguated via `raw > 1e12 ? ms : sec*1000`.
 */
export function getMapPointTimeMs(row: MapPointRow): number {
  const raw = row?.Datetime ?? row?.timestamp ?? row?.time ?? row?.datetime;
  if (raw == null) return NaN;
  if (raw instanceof Date) return raw.getTime();
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return NaN;
    return raw > 1e12 ? raw : raw * 1000;
  }
  const t = new Date(raw as string).getTime();
  return Number.isFinite(t) ? t : NaN;
}

/** First finite numeric value among the given keys, or NaN. */
function numFromRow(row: MapPointRow, keys: string[]): number {
  for (const k of keys) {
    const v = row?.[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v !== '' && !Number.isNaN(Number(v))) return Number(v);
  }
  return NaN;
}

/** Wrap an angle in degrees to (-180, 180]. */
function wrap180(deg: number): number {
  let d = ((deg + 180) % 360) - 180;
  if (d <= -180) d += 360;
  return d;
}

/**
 * True wind angle magnitude in degrees.
 *
 * The precomputed fleet-map `events_mapdata` blob does not always carry a TWA column, but it does
 * carry heading/course and true wind direction. When TWA is absent we derive it as
 * `|wrap180(TWD − course)|` (course = COG when present, else heading). |TWA| only feeds the
 * upwind/downwind decision, so the exact reference (heading vs course) is not critical.
 */
function absTwaDegFromRow(row: MapPointRow): number {
  const direct = numFromRow(row, ['Twa_deg', 'twa_deg', 'Twa', 'twa', 'TWA']);
  if (Number.isFinite(direct)) return Math.abs(direct);

  const twd = numFromRow(row, ['Twd_deg', 'twd_deg', 'Twd', 'twd', 'TWD']);
  const course = numFromRow(row, ['Cog_deg', 'cog_deg', 'Cog', 'cog', 'COG', 'Hdg_deg', 'hdg_deg', 'Hdg', 'hdg', 'HDG', 'Heading']);
  if (Number.isFinite(twd) && Number.isFinite(course)) {
    return Math.abs(wrap180(twd - course));
  }
  return NaN;
}

/** Truncated `Race_number` (any casing) or NaN when missing. */
export function raceNumberFromRow(row: MapPointRow): number {
  const raw = row?.Race_number ?? row?.race_number ?? row?.Race ?? row?.RACE;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.trunc(n) : NaN;
}

/** Truncated `leg_number` (any casing) or NaN when off-leg / missing. */
function legNumberFromRow(row: MapPointRow): number {
  const raw = row?.leg_number ?? row?.Leg_number ?? row?.Leg ?? row?.LEG;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.trunc(n) : NaN;
}

/** Boat speed magnitude in km/h, or NaN. */
function bspKphFromRow(row: MapPointRow): number {
  const b = bspMagnitudeInDisplayUnit(row as Record<string, unknown>, 'Bsp', SPEED_UNIT_KPH);
  return Number.isFinite(b) ? Math.abs(b) : NaN;
}

/**
 * VMG magnitude (always km/h, positive) for the integration; NaN when the row has no usable VMG.
 *
 * Falls back to `|Bsp · cos(TWA)|` (the same projection the upstream pipeline uses,
 * `Vmg = cos(CWA)·Bsp`) when the row has no VMG column but does have boat speed + a derivable TWA —
 * this is what makes the feature work on the compressed fleet `events_mapdata` blob, which carries
 * Bsp/Hdg/Twd but not always Vmg.
 */
function vmgKphMagnitudeFromRow(row: MapPointRow, vmgField: string): number {
  const v = vmgMagnitudeInDisplayUnit(row as Record<string, unknown>, vmgField, SPEED_UNIT_KPH);
  if (Number.isFinite(v)) return Math.abs(v);

  const bspKph = bspKphFromRow(row);
  const absTwa = absTwaDegFromRow(row);
  if (Number.isFinite(bspKph) && Number.isFinite(absTwa)) {
    return Math.abs(bspKph * Math.cos((absTwa * Math.PI) / 180));
  }
  return NaN;
}

/** True when this row's leg is measured on boat speed rather than |VMG| (see {@link BSP_LEG_NUMBERS}). */
function isBspLeg(row: MapPointRow): boolean {
  const leg = legNumberFromRow(row);
  return Number.isFinite(leg) && BSP_LEG_NUMBERS.includes(leg);
}

/**
 * Made-good speed (km/h) contributed by one row: BSP on the legs listed in {@link BSP_LEG_NUMBERS},
 * |VMG| on every other leg.
 *
 * The decision is a pure function of `Leg_number`, so every boat is measured the same way at the same
 * instant, and a row with no leg number never flips metric on its own. A BSP leg with no usable boat
 * speed falls through to |VMG| rather than dropping the sample.
 */
function madeGoodSpeedKphFromRow(row: MapPointRow, vmgField: string): number {
  if (isBspLeg(row)) {
    const bspKph = bspKphFromRow(row);
    if (Number.isFinite(bspKph)) return bspKph;
  }
  return vmgKphMagnitudeFromRow(row, vmgField);
}

export interface CumulativePoint {
  tMs: number;
  cumM: number;
  /**
   * Set when this sample's speed was unusable for longer than {@link MAX_SPEED_HOLD_MS}, so the
   * cumulative here is not a measurement. {@link interpolateCumulativeMeters} returns NaN across
   * such samples, which blanks the affected stretch of the timeline rather than drawing a
   * confident-looking gain or loss over data we do not have.
   */
  unmeasured?: boolean;
  /** `Race_number` of the row this sample came from; feeds the same-race gate. NaN when absent. */
  race?: number;
}

/**
 * `Race_number` the series was in at `tMs`, taken from the sample at or before it. NaN outside the
 * series' coverage or when the rows carry no race number.
 */
export function raceAtTime(series: CumulativePoint[], tMs: number): number {
  if (!series || series.length === 0) return NaN;
  if (tMs < series[0].tMs || tMs > series[series.length - 1].tMs) return NaN;
  let lo = 0;
  let hi = series.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (series[mid].tMs <= tMs) lo = mid;
    else hi = mid - 1;
  }
  const race = series[lo].race;
  return typeof race === 'number' && Number.isFinite(race) ? race : NaN;
}

/** Count of samples whose speed was unusable for longer than the hold window (for diagnostics). */
export function countUnmeasuredSamples(series: CumulativePoint[]): number {
  let n = 0;
  for (const p of series || []) {
    if (p.unmeasured) n++;
  }
  return n;
}

/**
 * Integrate made-good speed (left-Riemann) into cumulative **distance sailed** (meters) for one boat.
 *
 * Per-sample speed is the unsigned made-good magnitude — |VMG| on beating/running legs, and BSP on
 * the reaching legs in {@link BSP_LEG_NUMBERS} (where |VMG| collapses toward 0). No leg-objective /
 * sign machinery, so nothing to mis-sign. Comparing two boats' cumulative distances (see {@link applyReferenceVmgDeltaToGroups})
 * then answers "who has made more good progress along the course", which stays correct even when
 * boats are on different legs (an overtaking boat a leg ahead keeps accumulating and reads positive,
 * instead of being mis-signed as "going the wrong way"). Tradeoff: a boat sailing genuinely backward
 * toward a rounded mark adds distance rather than subtracting it — but the reference *difference*
 * still shows it losing whenever the reference is making good forward, so the net gain/loss direction
 * is preserved.
 *
 * The integral resets to 0 at the first in-window sample; intervals longer than the gap threshold
 * contribute 0 meters.
 */
export function buildCumulativeVmgSeries(
  sortedPoints: MapPointRow[],
  t0Ms: number,
  vmgField: string,
  gapThresholdMs: number = TIMELINE_VMG_GAP_THRESHOLD_MS
): CumulativePoint[] {
  const out: CumulativePoint[] = [];
  let cumM = 0;
  let prevT: number | null = null;
  let started = false;
  const gapSec = gapThresholdMs / 1000;
  const maxStepM = (MAX_MADE_GOOD_SPEED_KPH / 3.6) * gapSec;
  // Last plausible made-good speed, and when the current run of unusable samples began.
  let heldSpeedKph = NaN;
  let holdStartMs: number | null = null;

  for (const row of sortedPoints || []) {
    const tMs = getMapPointTimeMs(row);
    if (!Number.isFinite(tMs) || tMs < t0Ms) continue;
    const race = raceNumberFromRow(row);
    if (!started) {
      started = true;
      prevT = tMs;
      out.push({ tMs, cumM: 0, race });
      continue;
    }
    const dtSec = (tMs - (prevT as number)) / 1000;
    let unmeasured = false;
    if (dtSec > 0 && dtSec <= gapSec) {
      const rawKph = madeGoodSpeedKphFromRow(row, vmgField); // |VMG|, or BSP on a BSP_LEG_NUMBERS leg
      // A sample is usable only if it is a finite, physically possible made-good speed. Missing
      // (NaN) and implausible (sentinel / decode glitch) are the same thing here: unknown.
      const usable = Number.isFinite(rawKph) && rawKph >= 0 && rawKph <= MAX_MADE_GOOD_SPEED_KPH;
      let speedKph: number;
      if (usable) {
        speedKph = rawKph;
        heldSpeedKph = rawKph;
        holdStartMs = null;
      } else {
        if (holdStartMs == null) holdStartMs = tMs;
        // Bridge a short burst with the last known speed; beyond the hold window, admit we don't know.
        speedKph = tMs - holdStartMs <= MAX_SPEED_HOLD_MS ? heldSpeedKph : NaN;
      }
      if (Number.isFinite(speedKph)) {
        // Two bounds, both required: the speed guard above, and this absolute per-sample ceiling so
        // no single row can ever move the curve by more than MAX_SAMPLE_DISTANCE_M.
        cumM += Math.min(speedKph * KPH_TO_MPS * dtSec, maxStepM);
      } else {
        unmeasured = true;
      }
    }
    prevT = tMs;
    out.push(unmeasured ? { tMs, cumM, race, unmeasured: true } : { tMs, cumM, race });
  }
  return out;
}

/**
 * Linear interpolation of cumulative meters at an arbitrary time (clamped to the series endpoints).
 *
 * Returns NaN when either sample bracketing `tMs` is `unmeasured` — the cumulative is not a
 * measurement there, so the caller must blank that stretch rather than plot it. This is what keeps a
 * boat (or the reference boat) with a bad data run from showing a large fake gain or loss.
 */
export function interpolateCumulativeMeters(series: CumulativePoint[], tMs: number): number {
  if (!series || series.length === 0) return NaN;
  if (tMs <= series[0].tMs) return series[0].unmeasured ? NaN : series[0].cumM;
  const last = series[series.length - 1];
  if (tMs >= last.tMs) return last.unmeasured ? NaN : last.cumM;
  // Binary search for the segment containing tMs.
  let lo = 0;
  let hi = series.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (series[mid].tMs < tMs) lo = mid + 1;
    else hi = mid;
  }
  const b = series[lo];
  const a = series[lo - 1];
  if (a.unmeasured || b.unmeasured) return NaN;
  const span = b.tMs - a.tMs;
  if (span <= 0) return b.cumM;
  const frac = (tMs - a.tMs) / span;
  return a.cumM + (b.cumM - a.cumM) * frac;
}

/**
 * Enrich every group's rows with `_vmgDeltaM`: how many meters of made-good progress this boat has
 * gained on (positive) or lost to (negative) the reference boat. The reference boat's own rows get 0.
 *
 * `warmupMs` (default {@link REFERENCE_DELTA_WARMUP_MS} = 2 min) holds every boat at exactly 0 for the
 * head of the window and moves the integration start to `t0Ms + warmupMs`; pass 0 to integrate from
 * t0 itself.
 *
 * Two boats are only comparable where they were actually doing the same thing at the same time, so a
 * row reads NaN (the timeline breaks the line) unless both hold:
 *
 * 1. **Shared coverage.** The delta is a difference of *increments over a window both boats span*:
 *    `(boat(t) − boat(t0)) − (ref(t) − ref(t0))` with `t0` the later of the two first samples, and
 *    nothing outside the reference boat's own coverage. Without this, a boat whose data starts before
 *    the reference's banks every meter it sailed while the reference had no samples at all — the
 *    clamped reference series reads 0 there, so it looks like the reference stood still. On
 *    2026-06-21 that put five boats at ~3000 m from a reference they were 230 m away from.
 * 2. **Same race.** Made-good distance in one race says nothing about made-good distance in another,
 *    however well the clocks line up. Gated on `Race_number` when both rows carry one; when either
 *    side has no race number the gate is skipped rather than blanking everything.
 *
 * Returns NEW group objects with cloned rows; inputs are not mutated. If the reference group is
 * absent the original groups are returned unchanged (no-op); if it has no samples at/after the
 * integration start (e.g. a window shorter than the warmup) every boat is flat at 0.
 */
export function applyReferenceVmgDeltaToGroups(
  groups: SourceGroup[],
  referenceSourceId: number,
  t0Ms: number,
  vmgField: string,
  warmupMs: number = REFERENCE_DELTA_WARMUP_MS
): SourceGroup[] {
  if (!Array.isArray(groups) || groups.length === 0) return groups;
  const refGroup = groups.find((g) => Number(g.sourceId) === Number(referenceSourceId));
  if (!refGroup) return groups;

  const startMs = t0Ms + (Number.isFinite(warmupMs) ? Math.max(0, warmupMs) : 0);
  const zeroed = (group: SourceGroup): SourceGroup => ({
    ...group,
    data: group.data.map((row) => ({ ...row, _vmgDeltaM: 0 })),
  });

  const refSeries = buildCumulativeVmgSeries(refGroup.data, startMs, vmgField);
  // Whole window sits inside the warmup (or the reference boat has no data past it): everyone flat at 0.
  if (refSeries.length === 0) return groups.map(zeroed);

  return groups.map((group) => {
    const isRef = Number(group.sourceId) === Number(referenceSourceId);
    if (isRef) return zeroed(group);

    const boatSeries = buildCumulativeVmgSeries(group.data, startMs, vmgField);
    const shared = sharedWindow(boatSeries, refSeries);
    const data = group.data.map((row) => ({
      ...row,
      _vmgDeltaM: referenceDeltaForRow(row, boatSeries, refSeries, shared, startMs),
    }));
    // A boat that is never comparable to this reference — no shared coverage, or a different race the
    // whole way — has no zero to hold either. Drop its warmup zeros too, so it is absent from the
    // chart rather than drawing a stub flat line that reads as "level with the reference".
    const everComparable = data.some(
      (row) => Number.isFinite(row._vmgDeltaM) && getMapPointTimeMs(row) >= startMs
    );
    return {
      ...group,
      data: everComparable ? data : data.map((row) => ({ ...row, _vmgDeltaM: NaN })),
    };
  });
}

/** The time span over which a boat and the reference are both measured, or null when they never overlap. */
interface SharedWindow {
  startMs: number;
  endMs: number;
  /** Each series' cumulative at `startMs`, subtracted so the comparison starts from a common zero. */
  boatBaseM: number;
  refBaseM: number;
}

function sharedWindow(boatSeries: CumulativePoint[], refSeries: CumulativePoint[]): SharedWindow | null {
  if (boatSeries.length === 0 || refSeries.length === 0) return null;
  const startMs = Math.max(boatSeries[0].tMs, refSeries[0].tMs);
  const endMs = Math.min(boatSeries[boatSeries.length - 1].tMs, refSeries[refSeries.length - 1].tMs);
  if (!(startMs <= endMs)) return null; // coverage never overlaps at all
  return {
    startMs,
    endMs,
    boatBaseM: interpolateCumulativeMeters(boatSeries, startMs),
    refBaseM: interpolateCumulativeMeters(refSeries, startMs),
  };
}

/**
 * One row's delta against the reference: meters gained (+) or lost (−) since the start of the window
 * the two boats share, or NaN where they are not comparable. See
 * {@link applyReferenceVmgDeltaToGroups} for the two conditions.
 */
function referenceDeltaForRow(
  row: MapPointRow,
  boatSeries: CumulativePoint[],
  refSeries: CumulativePoint[],
  shared: SharedWindow | null,
  integrationStartMs: number
): number {
  const tMs = getMapPointTimeMs(row);
  if (!Number.isFinite(tMs)) return NaN;
  if (tMs < integrationStartMs) return 0; // warmup: no delta yet
  if (!shared) return NaN;
  // Outside the shared window there is no reference to measure against.
  if (tMs < shared.startMs || tMs > shared.endMs) return NaN;
  // Comparing progress made in different races is meaningless even when the clocks overlap.
  const rowRace = raceNumberFromRow(row);
  const refRace = raceAtTime(refSeries, tMs);
  if (Number.isFinite(rowRace) && Number.isFinite(refRace) && rowRace !== refRace) return NaN;

  const boatCum = interpolateCumulativeMeters(boatSeries, tMs);
  const refCum = interpolateCumulativeMeters(refSeries, tMs);
  if (!Number.isFinite(boatCum) || !Number.isFinite(refCum)) return NaN;
  if (!Number.isFinite(shared.boatBaseM) || !Number.isFinite(shared.refBaseM)) return NaN;
  return (boatCum - shared.boatBaseM) - (refCum - shared.refBaseM);
}
